import React, { useRef, useState } from 'react';
import { Calendar as CalendarIcon, Clock } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { formatDeadline } from '../../../lib/project-utils';
import FloatingDatePicker from '../../../components/shared/FloatingDatePicker';
import { logger } from '../../../utils/logger';
import { useViewport } from '../../../contexts/ViewportContext';

interface TaskFormDeadlineProps {
  selectedDate: Date | null;
  onDateChange: (date: Date | null) => void;
  taskId?: string;
  taskStatus?: string;
  isEditMode?: boolean;
}

export const TaskFormDeadline: React.FC<TaskFormDeadlineProps> = ({
  selectedDate,
  onDateChange,
  taskId,
  taskStatus,
  isEditMode = false,
}) => {
  const { isMobile } = useViewport();
  const dateButtonRef = useRef<HTMLButtonElement>(null);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const handleDateButtonClick = () => {
    if (isEditMode) {
      logger.component('TaskForm dateButton click - opening date picker', {
        isTaskForm: true,
        isEditMode,
        taskId,
      });
    }
    setIsDatePickerOpen(true);
  };

  const handleDateChange = (date: Date) => {
    if (isEditMode) {
      logger.component('TaskForm onDateChange', {
        taskId,
        dateString: date.toISOString(),
        dateObject: date,
      });
    } else {
      logger.component('TaskForm: date selected in new task form', {
        dateValue: date.toISOString(),
        dateObject: date,
      });
    }

    // Create a fresh Date object to avoid any reference issues
    const newDate = new Date(date.getTime());
    // Set hours to noon to avoid timezone issues
    newDate.setHours(12, 0, 0, 0);

    onDateChange(newDate);
    setIsDatePickerOpen(false);
  };

  const handleClearDate = () => {
    logger.component(isEditMode ? 'TaskForm onClear' : 'TaskForm: clearing deadline in new task form', {
      taskId,
    });
    
    onDateChange(null);
    setIsDatePickerOpen(false);
  };

  // Determine button text and style based on deadline
  const getButtonTextAndStyle = () => {
    if (!selectedDate) return { 
      text: isEditMode ? 'Add Deadline' : 'Add deadline', 
      buttonClass: 'text-blue-700',
      icon: <CalendarIcon size={14} className="flex-shrink-0 mr-1.5" />
    };

    const deadlineInfo = formatDeadline(selectedDate.toISOString());
    if (!deadlineInfo) return { 
      text: isEditMode ? 'Set Deadline' : 'Set deadline',
      buttonClass: 'text-blue-700',
      icon: <CalendarIcon size={14} className="flex-shrink-0 mr-1.5" />
    };

    // For edit mode with specific deadline text
    if (isEditMode && deadlineInfo.lateByText && taskStatus !== 'done') {
      // Specifically check for "Due today" text to apply green
      if (deadlineInfo.lateByText === 'Due today') {
        return {
          text: deadlineInfo.lateByText,
          buttonClass: 'text-green-600 font-bold',
          icon: <Clock size={14} className="flex-shrink-0 mr-1.5 text-green-600 stroke-[3]" />
        };
      } else if (deadlineInfo.isOverdue) {
        return {
          text: deadlineInfo.lateByText,
          buttonClass: 'text-red-600 font-bold',
          icon: <Clock size={14} className="flex-shrink-0 mr-1.5 text-red-600 stroke-[3]" />
        };
      }
    }

    // Default formatted date display with appropriate styling
    if (deadlineInfo.isToday && taskStatus !== 'done') {
      return {
        text: deadlineInfo.formatted,
        buttonClass: 'text-green-600',
        icon: <CalendarIcon size={14} className="flex-shrink-0 mr-1.5" />
      };
    } else if (deadlineInfo.isOverdue && taskStatus !== 'done') {
      return {
        text: deadlineInfo.formatted,
        buttonClass: 'text-red-600 font-bold',
        icon: <CalendarIcon size={14} className="flex-shrink-0 mr-1.5" />
      };
    }

    return {
      text: deadlineInfo.formatted,
      buttonClass: 'text-blue-700',
      icon: <CalendarIcon size={14} className="flex-shrink-0 mr-1.5" />
    };
  };

  const { text, buttonClass, icon } = getButtonTextAndStyle();

  return (
    <div className="relative mt-4">
      <div className="flex items-center">
        <button
          ref={dateButtonRef}
          type="button"
          onClick={handleDateButtonClick}
          className={cn('flex items-center p-0', buttonClass)}
        >
          {icon}
          <span>{text}</span>
        </button>
      </div>

      {isDatePickerOpen && (
        <FloatingDatePicker
          selectedDate={selectedDate}
          positionSource={
            isMobile
              ? { type: 'fullscreen' }
              : {
                  type: 'buttonRef',
                  ref: dateButtonRef,
                  positionAtClick: true,
                }
          }
          launchedFromMenu={true}
          onDateChange={handleDateChange}
          onClose={() => setIsDatePickerOpen(false)}
          onClear={handleClearDate}
        />
      )}
    </div>
  );
};